import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.35 + i * 0.16, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fade = (delay) => ({
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] } },
});

export default function KineticHero({ onBook }) {
  const ref = useRef(null);
  const [priceLabel, setPriceLabel] = useState("£12.50 / person");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dates`)
      .then((r) => r.json())
      .then(({ dates }) => {
        if (!dates || dates.length === 0) return;
        const prices = dates.map((d) => d.price_pence ?? 1250);
        const min = Math.min(...prices);
        const fmt = `£${(min / 100).toFixed(2)}`;
        setPriceLabel(prices.every((p) => p === min) ? `${fmt} / person` : `From ${fmt} / person`);
      })
      .catch(() => {});
  }, []);
  const glassY = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const glassRotate = useTransform(scrollYProgress, [0, 1], [0, 6]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden px-6 sm:px-10 lg:px-16 pt-28 pb-16">
      <div className="absolute inset-0 spotlight pointer-events-none" aria-hidden="true" />

      {/* floating glass with parallax */}
      <motion.img
        src={ASSETS.glass}
        alt="Engraved beer tasting glass"
        style={{ y: glassY, rotate: glassRotate }}
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 0.9, scale: 1 }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        className="hidden lg:block absolute right-[6%] top-1/2 -translate-y-1/2 w-[280px] xl:w-[340px] object-contain z-0 select-none"
        data-testid="hero-glass-image"
      />

      <div className="relative z-10 mx-auto max-w-[1400px]">
        <motion.div
          variants={fade(0.15)} initial="hidden" animate="show"
          className="flex flex-col items-center text-center"
        >
          <img src={ASSETS.crest} alt="Hop and barley crest" className="w-24 sm:w-28 mb-6 rounded-full ring-1 ring-[rgba(201,162,74,0.35)]" />
          <p className="text-[var(--gold)] uppercase tracking-[0.4em] text-[11px] sm:text-xs mb-2">The Horseshoe · Chipping Sodbury</p>
        </motion.div>

        <motion.h1 style={{ y: titleY }} className="font-display text-center leading-[0.82] mt-2">
          <span className="block overflow-hidden">
            <motion.span custom={0} variants={lineVariants} initial="hidden" animate="show"
              className="block text-[19vw] sm:text-[16vw] lg:text-[13vw] text-[var(--cream)] font-medium">
              BEER
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span custom={1} variants={lineVariants} initial="hidden" animate="show"
              className="block text-[15vw] sm:text-[13vw] lg:text-[11vw] text-[var(--gold)] font-medium tracking-tight">
              EXPLAINED
            </motion.span>
          </span>
        </motion.h1>

        <motion.p variants={fade(1.1)} initial="hidden" animate="show"
          className="font-serif-alt italic text-center text-2xl sm:text-3xl text-[var(--cream)] mt-6">
          A Talk &amp; Tasting with Matthew Lewis
        </motion.p>

        <motion.div variants={fade(1.3)} initial="hidden" animate="show"
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-[var(--muted)]">
          <span className="uppercase tracking-[0.25em] text-xs sm:text-sm">Multiple Dates</span>
          <span className="hidden sm:block text-[var(--gold)]">◆</span>
          <span className="uppercase tracking-[0.25em] text-xs sm:text-sm">{priceLabel}</span>
        </motion.div>

        <motion.div variants={fade(1.5)} initial="hidden" animate="show" className="mt-12 flex justify-center">
          <button
            data-testid="hero-book-btn"
            onClick={onBook}
            className="group relative rounded-full border border-[var(--gold)] bg-[var(--gold)] text-[var(--bg)] px-10 py-4 uppercase tracking-[0.2em] text-xs font-semibold overflow-hidden transition-colors duration-300 hover:bg-transparent hover:text-[var(--gold)]"
          >
            Book your tickets
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[var(--gold)] z-10"
      >
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }}>
          <ArrowDown className="w-5 h-5" />
        </motion.div>
      </motion.div>
    </section>
  );
}
